/* eslint-disable @typescript-eslint/no-unused-vars */
import { hudAssets } from "assets/cdn/hud";
import { useGoogleAnalyticsContext, useWebStoreContext } from "contexts";
import { useAppSelector, useImagePath } from "hooks";
import { useLinks } from "hooks/useLinks";
import useLiveopsConfigs from "hooks/useLiveopsConfigs";
import useNewDay from "hooks/useNewDay";
import { HudDataResponse } from "interfaces";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useGetHudDataQuery } from "redux/apis";
import { selectDipState } from "redux/slices";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";
import { Autoplay, EffectFade, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { SwiperOptions } from "swiper/types";
import useWindowFocus from "use-window-focus";
import { getHudAnalyticsValues, shouldReportHudOfferShown } from "./helper";
import { HudItem } from "./lib";
import { HudContainer } from "./lib/styles";

const HUD_CONFIG_IDS = [
	"hud_item_display_count",
	"hud_rotation_interval_seconds",
	"hud_daily_gift_disabled"
];

export const Hud = () => {
	const links = useLinks();
	const location = useLocation();
	const { newDay } = useNewDay();
	const hudDipWasOpened = useRef(false);
	const isWindowFocused = useWindowFocus();
	const { getImage } = useImagePath("cdn");
	const userNavigationPending = useRef(false);
	const activeDot = getImage(hudAssets.dotActive);
	const inactiveDot = getImage(hudAssets.dotInactive);
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const isLandingPage = location.pathname === links.home.path;
	const { authToken, nowTime, userTracker } = useWebStoreContext();
	const isDailyGiftsPage = location.pathname === links.free_daily_gifts.path;
	const { opened: isDipOpened, currentDip } = useAppSelector(selectDipState);

	const pollingTime = 30 * 1000;
	const {
		data: hudData,
		isLoading,
		refetch
	}: {
		data?: HudDataResponse;
		isLoading: boolean;
		refetch: () => void;
	} = useGetHudDataQuery("", {
		skip: isLandingPage || !authToken?.value || !isWindowFocused,
		pollingInterval: pollingTime
	});
	const configs = useLiveopsConfigs(HUD_CONFIG_IDS);
	const configuredItemDisplayCount = Number.parseInt(
		configs.hud_item_display_count,
		10
	);
	const configuredRotationIntervalSeconds = Number.parseFloat(
		configs.hud_rotation_interval_seconds
	);
	const itemDisplayCount =
		configuredItemDisplayCount > 0 ? configuredItemDisplayCount : 5;
	const rotationIntervalSeconds =
		configuredRotationIntervalSeconds > 0
			? configuredRotationIntervalSeconds
			: 2;
	const hudItems =
		[...(hudData?.data?.dipConfigs ?? [])]
			.sort(
				(a, b) => (a.dipConfig?.priority ?? 0) - (b.dipConfig?.priority ?? 0)
			)
			.slice(0, itemDisplayCount) ?? [];
	const slidesKey = hudItems.map(({ dipConfig }) => dipConfig?.id).join("|");
	const hudDipIds = useMemo(
		() => new Set(hudItems.map(({ dipConfig }) => dipConfig?.id)),
		[slidesKey]
	);
	const refreshHud = useCallback(() => {
		refetch();
	}, [refetch]);
	const reportHudOfferShown = (index: number) => {
		const hudItem = hudItems[index];

		if (
			!hudItem ||
			!shouldReportHudOfferShown(hudItem, userTracker?.id, nowTime)
		) {
			return;
		}

		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "limited_deal_hud_show",
			event_type: "limited_deal_hud",
			action: "hud_show",
			source: "limited_deal",
			...getHudAnalyticsValues(hudItem, nowTime),
			page_location: location.pathname
		});
	};
	const reportHudNavigation = () => {
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "limited_deal_hud_click",
			event_type: "limited_deal_hud",
			action: "hud_click",
			source: "limited_deal",
			page_location: location.pathname
		});
	};

	const shouldHideHud =
		isLandingPage ||
		(isDailyGiftsPage && hudData?.data?.showInDailyGifts !== true);

	const swiperParams: SwiperOptions = {
		autoplay: {
			enabled: true,
			delay: rotationIntervalSeconds * 1000,
			pauseOnMouseEnter: true,
			disableOnInteraction: false,
			waitForTransition: false
		},
		effect: "fade",
		fadeEffect: {
			crossFade: true
		},
		virtualTranslate: true,
		watchSlidesProgress: true,
		speed: 300,
		modules: [Autoplay, EffectFade, Pagination],
		pagination: {
			clickable: true,
			dynamicBullets: false,
			renderBullet: (index, className) =>
				`<button class="${className}" aria-label="Go to HUD item ${
					index + 1
				}"></button>`
		},
		loop: false,
		rewind: false,
		slidesPerView: 1,
		spaceBetween: 0,
		slidesOffsetAfter: 0,
		allowTouchMove: true,
		simulateTouch: true,
		grabCursor: true,
		threshold: 5,
		preventClicks: true,
		preventClicksPropagation: true
	};

	useEffect(() => {
		if (newDay) {
			refreshHud();
		}
	}, [newDay, refreshHud]);

	useEffect(() => {
		if (!shouldHideHud && hudItems.length > 0) {
			reportHudOfferShown(0);
		}
	}, [slidesKey, shouldHideHud, userTracker?.id, !!nowTime]);

	useEffect(() => {
		if (isDipOpened && currentDip?.id && hudDipIds.has(currentDip.id)) {
			hudDipWasOpened.current = true;
			return;
		}

		if (!isDipOpened && hudDipWasOpened.current) {
			hudDipWasOpened.current = false;
			refreshHud();
		}
	}, [isDipOpened, currentDip?.id, hudDipIds, refreshHud]);

	if (shouldHideHud || !authToken?.value || hudItems.length === 0) {
		return null;
	}

	return (
		<HudContainer $activeDot={activeDot} $inactiveDot={inactiveDot}>
			{hudItems.length > 0 && (
				<Swiper
					key={slidesKey}
					{...swiperParams}
					onClickCapture={(event) => {
						const target = event.target as HTMLElement;
						if (target.closest(".swiper-pagination-bullet")) {
							userNavigationPending.current = true;
						}
					}}
					onTouchStart={() => {
						userNavigationPending.current = true;
					}}
					onTouchEnd={() => {
						window.setTimeout(() => {
							userNavigationPending.current = false;
						}, 0);
					}}
					onSlideChange={(swiper) => {
						reportHudOfferShown(swiper.realIndex);
						if (userNavigationPending.current) {
							reportHudNavigation();
							userNavigationPending.current = false;
						}
					}}
				>
					{hudItems.map((hudItem) => (
						<SwiperSlide key={hudItem.dipConfig?.id}>
							<HudItem hudItem={hudItem} onExpire={refreshHud} />
						</SwiperSlide>
					))}
				</Swiper>
			)}
		</HudContainer>
	);
};
